/* Optional 4X4 feature badge for Mode's existing product photos.
 * Only our decorative overlay nodes are added; native media and controls stay in place.
 */
(() => {
  'use strict';

  const GLOBAL = 'BosscoPressImageBadge';
  const SOURCE = '[data-bc-photo-badge-section]';
  const PHOTO = '.main-image .product-slideshow .product-media--image';
  const RELEVANT = `${SOURCE}, [data-section-type="main-product"], .product-media--image`;
  const instances = new Map();
  const unloaded = new WeakSet();
  let frame = 0;

  if (window[GLOBAL]) {
    window[GLOBAL].scan();
    return;
  }

  function findHost(source) {
    const productId = source.dataset.bcPhotoBadgeProduct;
    if (!productId) return null;
    for (const section of document.querySelectorAll('main [data-section-type="main-product"]')) {
      const form = section.querySelector('.product-detail > .detail > .product-form[data-product-id], .product-detail > .product-form[data-product-id]');
      if (form?.dataset.productId === productId) {
        return section.querySelector('.product-detail > .gallery.product-column-left > .gallery__inner');
      }
    }
    return null;
  }

  class PhotoBadge {
    constructor(source) {
      this.source = source;
      this.id = source.dataset.bcPhotoBadgeSection;
      this.template = source.querySelector('[data-bc-photo-badge-template]');
      this.host = null;
      this.photos = new Map();
    }

    status(value) {
      this.source.dataset.bcPhotoBadgeState = value;
      const notice = this.source.querySelector('[data-bc-photo-badge-notice]');
      if (notice) notice.textContent = value === 'disabled'
        ? 'The 4X4 Australia image badge is switched off. Enable it in this section’s settings.'
        : 'The 4X4 Australia badge appears over your main product photos. Preview a product with images to see it.';
    }

    sync() {
      if (this.source.dataset.bcPhotoBadgeEnabled !== 'true') {
        this.unmount();
        this.status('disabled');
        return;
      }
      const host = findHost(this.source);
      if (host !== this.host) this.unmount();
      this.host = host;
      if (!host || !this.template?.content.firstElementChild) {
        this.status('missing');
        return;
      }

      const current = new Set(host.querySelectorAll(PHOTO));
      for (const [photo, badge] of this.photos) {
        if (!current.has(photo)) this.release(photo, badge);
      }
      for (const photo of current) {
        const owner = photo.dataset.bcPhotoBadgeOwner;
        if (owner && owner !== this.id) continue;
        // Slick may clone a decorated slide. Reuse the cloned overlay rather than duplicating it.
        let badge = this.photos.get(photo);
        if (!badge || badge.parentElement !== photo) {
          const existing = Array.from(photo.children).filter((node) => node.dataset.bcPhotoBadgeOwner === this.id);
          badge = existing.shift() || this.template.content.firstElementChild.cloneNode(true);
          for (const duplicate of existing) duplicate.remove();
          badge.dataset.bcPhotoBadgeOwner = this.id;
          if (badge.parentElement !== photo) photo.append(badge);
        }
        photo.classList.add('bc-photo-badge-host');
        photo.dataset.bcPhotoBadgeOwner = this.id;
        this.photos.set(photo, badge);
      }
      this.status(this.photos.size ? 'mounted' : 'missing');
    }

    release(photo, badge) {
      badge.remove();
      if (photo.dataset.bcPhotoBadgeOwner === this.id) {
        photo.classList.remove('bc-photo-badge-host');
        delete photo.dataset.bcPhotoBadgeOwner;
      }
      this.photos.delete(photo);
    }

    unmount() {
      for (const [photo, badge] of this.photos) this.release(photo, badge);
      // Remove freshly cloned badges even if an editor unload arrives before the next scan.
      if (this.host) {
        for (const photo of this.host.querySelectorAll(PHOTO)) {
          if (photo.dataset.bcPhotoBadgeOwner !== this.id) continue;
          for (const child of Array.from(photo.children)) {
            if (child.dataset.bcPhotoBadgeOwner === this.id) child.remove();
          }
          photo.classList.remove('bc-photo-badge-host');
          delete photo.dataset.bcPhotoBadgeOwner;
        }
      }
      this.host = null;
    }
  }

  function scan() {
    frame = 0;
    for (const [source, instance] of instances) {
      if (!source.isConnected || unloaded.has(source)) {
        instance.unmount();
        instances.delete(source);
      }
    }
    for (const source of document.querySelectorAll(SOURCE)) {
      if (unloaded.has(source)) continue;
      let instance = instances.get(source);
      if (!instance) {
        instance = new PhotoBadge(source);
        instances.set(source, instance);
      }
      instance.sync();
    }
  }

  function scheduleScan() {
    if (!frame) frame = window.requestAnimationFrame(scan);
  }

  function onUnload(event) {
    for (const [source, instance] of instances) {
      if (event.target.contains(source)) {
        unloaded.add(source);
        instance.unmount();
        instances.delete(source);
      } else if (instance.host && event.target.contains(instance.host)) {
        instance.unmount();
      }
    }
  }

  function start() {
    scan();
    const observer = new MutationObserver((records) => {
      if (records.some((record) => [...record.addedNodes, ...record.removedNodes].some((node) =>
        node.nodeType === 1 && (node.matches(RELEVANT) || node.querySelector(RELEVANT))
      ))) scheduleScan();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', (event) => {
      for (const source of event.target.querySelectorAll(SOURCE)) unloaded.delete(source);
      scheduleScan();
    });
    document.addEventListener('shopify:section:unload', onUnload);
    document.addEventListener('shopify:section:reorder', scheduleScan);
    document.addEventListener('shopify:section:select', (event) => {
      scan();
      for (const [source, instance] of instances) {
        if (event.target.contains(source)) {
          const firstPhoto = Array.from(instance.photos.keys()).find((photo) => photo.getClientRects().length);
          firstPhoto?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
        }
      }
    });
    window.addEventListener('load', scheduleScan, { once: true });
  }

  window[GLOBAL] = { scan: scheduleScan };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
